"use client"

import { useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "@/shared/lib/query-keys"
import { useAuthStore } from "@/features/auth/store"
import { useUIStore, type PaywallIntent } from "@/shared/stores/ui-store"
import { resolvePaywallCharacterFields } from "@/features/credits/lib/paywall-context"
import { useCoinBalance } from "./use-credit-balance"
import {
  COIN_COSTS,
  TIER_CONFIG,
  isPaidTier,
  tierHasFeature,
  type CoinActionType,
} from "@/features/credits/constants"
import {
  deductCoins,
  checkFreeUserLimits,
} from "@/features/credits/actions"

export type CoinCheckAction =
  | "message"
  | "chat_image"
  | "image_generation"
  | "tts"
  | "phone_call"
  | "character_creation"

interface CoinCheckResult {
  allowed: boolean
  reason?: string
}

/**
 * Core coins hook. Provides:
 * - `canUse(action)` — checks if the user can perform an action (free limits or coin balance)
 *   and auto-opens the appropriate paywall modal if not.
 * - `deduct(action, referenceId?)` — deducts coins for the action.
 * - `coins` — current balance from cache.
 * - `tier` — current subscription tier.
 */
export function useCoins() {
  const user = useAuthStore((s) => s.user)
  const tier = user?.subscription ?? "free"
  const { coins, refetch } = useCoinBalance()
  const queryClient = useQueryClient()
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const setPaywallContext = useUIStore((s) => s.setPaywallContext)

  const openSubscribePaywall = useCallback(
    (intent: PaywallIntent, trigger: string) => {
      const existing = useUIStore.getState().paywallContext
      setPaywallContext({
        intent,
        trigger,
        ...resolvePaywallCharacterFields(intent, existing),
      })
      setActiveModal("subscribe-paywall")
    },
    [setActiveModal, setPaywallContext]
  )

  /**
   * Check if the user can perform a coin-consuming action.
   * - Free users: checks hard limits (messages/day, images/month, feature gating)
   * - Paid users: checks coin balance
   *
   * If the user cannot proceed, the appropriate paywall modal is opened.
   * Returns { allowed, reason }.
   */
  const canUse = useCallback(
    async (action: CoinCheckAction, costOverride?: number): Promise<CoinCheckResult> => {
      if (!user) {
        setActiveModal("register")
        return { allowed: false, reason: "Not authenticated" }
      }

      // ── Free user checks ──────────────────────────────────────────────
      if (!isPaidTier(tier)) {
        // Voice / phone features require subscription
        if (action === "tts" || action === "phone_call") {
          openSubscribePaywall(
            action === "tts" ? "voice_message" : "phone_call",
            `${action}_locked`
          )
          return { allowed: false, reason: "This feature requires a subscription" }
        }

        // For messages, images, and character creation, check server-side limits
        if (
          action === "message" ||
          action === "chat_image" ||
          action === "image_generation" ||
          action === "character_creation"
        ) {
          const result = await checkFreeUserLimits(action)
          if (!result.success || !result.limitResult?.allowed) {
            openSubscribePaywall(getPaywallIntent(action), result.limitResult?.reason ?? `${action}_limit`)
            return {
              allowed: false,
              reason: result.limitResult?.reason ?? "Limit reached",
            }
          }
          return { allowed: true }
        }

        // Default: free users can't use unknown coin actions
        openSubscribePaywall("generic", "subscription_required")
        return { allowed: false, reason: "This feature requires a subscription" }
      }

      // ── Paid user checks ──────────────────────────────────────────────

      // Feature gating by tier. Current paid plans include phone calls.
      if (action === "phone_call" && !tierHasFeature(tier, "phone_call")) {
        openSubscribePaywall("phone_call", "phone_call_plan_required")
        return {
          allowed: false,
          reason: "Phone calls are not available on this plan",
        }
      }

      if (action === "tts" && !tierHasFeature(tier, "voice")) {
        openSubscribePaywall("voice_message", "voice_plan_required")
        return {
          allowed: false,
          reason: "Voice notes require a higher plan",
        }
      }

      // Messages are unlimited for paid users
      if (action === "message") {
        return { allowed: true }
      }

      // Character creation: paid users need 40 coins
      if (action === "character_creation") {
        const cost = COIN_COSTS.character_creation
        if (cost > 0 && coins < cost) {
          setActiveModal("buy-credits-paywall")
          return {
            allowed: false,
            reason: `Insufficient coins. Character creation costs ${cost} coins. You have ${coins}.`,
          }
        }
        return { allowed: true }
      }

      // Other coin-consuming actions: check balance
      // costOverride allows variable-cost actions (e.g. image_generation × numImages)
      const cost = costOverride ?? getCostForAction(action)
      if (cost > 0 && coins < cost) {
        setActiveModal("buy-credits-paywall")
        return {
          allowed: false,
          reason: `Insufficient coins. You need ${cost} coins but have ${coins}.`,
        }
      }

      return { allowed: true }
    },
    [user, tier, coins, setActiveModal, openSubscribePaywall]
  )

  /**
   * Deduct coins for a completed action.
   * Should be called AFTER the action succeeds (not before).
   * Invalidates the balance cache.
   */
  const deduct = useCallback(
    async (
      action: CoinActionType,
      referenceId?: string,
      description?: string,
      costOverride?: number
    ) => {
      if (!user || !isPaidTier(tier)) return

      const cost = costOverride ?? COIN_COSTS[action]
      if (!cost) return

      const result = await deductCoins(cost, action, referenceId, description)

      // Refresh balance cache
      if (user) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.coins.balance(user.id),
        })
      }

      return result
    },
    [user, tier, queryClient]
  )

  return {
    canUse,
    deduct,
    coins,
    tier,
    isPaid: isPaidTier(tier),
    refetchBalance: refetch,
    tierConfig: TIER_CONFIG[tier],
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function getCostForAction(action: CoinCheckAction): number {
  switch (action) {
    case "chat_image":
      return COIN_COSTS.chat_image
    case "image_generation":
      return COIN_COSTS.image_generation
    case "tts":
      return COIN_COSTS.tts
    case "phone_call":
      return COIN_COSTS.phone_call_per_minute
    case "message":
      return 0
    default:
      return 0
  }
}

function getPaywallIntent(action: CoinCheckAction): PaywallIntent {
  switch (action) {
    case "message":
      return "chat_message"
    case "chat_image":
      return "chat_image"
    case "image_generation":
      return "image_generation"
    case "tts":
      return "voice_message"
    case "phone_call":
      return "phone_call"
    case "character_creation":
      return "character_creation"
    default:
      return "generic"
  }
}
